import React from 'react';
import { Checkbox, Divider, List, ListProps, Tooltip } from 'antd';
import styled from 'styled-components';
import { ANTD_GRAY } from '../entity/shared/constants';
import { SEPARATE_SIBLINGS_URL_PARAM } from '../entity/shared/siblingUtils';
import { CompactEntityNameList } from '../recommendations/renderer/component/CompactEntityNameList';
import { useEntityRegistry } from '../useEntityRegistry';
import { EntityType, SearchResult, SearchSuggestion } from '../../types.generated';
import analytics, { EventType } from '../analytics';
import { EntityAndType } from '../entity/shared/types';
import { useIsSearchV2 } from './useSearchAndBrowseVersion';
import { CombinedSearchResult } from './utils/combineSiblingsInSearchResults';
import EmptySearchResults from './EmptySearchResults';
import { useTranslation } from '../../i18n/useTranslation';

const ResultList = styled(List)<{ isSelectMode: boolean }>`
    &&& {
        height: ${(props) => `calc(100% - ${props.isSelectMode ? '46px' : '0px'})`};
        width: 100%;
        border-color: ${(props) => props.theme.styles['border-color-base']};
        padding: 0px;
        border-radius: 0px;

        @media (max-width: 991px) {
            padding: 8px;
            height: auto;
        }
    }
`;

const StyledCheckbox = styled(Checkbox)`
    margin-right: 12px;
`;

const ThinDivider = styled(Divider)`
    margin-top: 16px;
    margin-bottom: 16px;
`;

export const ResultWrapper = styled.div<{
    margin?: string;
    padding?: string;
    borderBottom?: string;
    showUpdatedStyles: boolean;
}>`
    ${(props) =>
        props.showUpdatedStyles &&
        `    
        background-color: white;
        padding: ${props.padding || '24px'};
        margin: ${props.margin || '0px auto 0px auto'};
        border-bottom: ${props.borderBottom || `1px solid ${ANTD_GRAY[13]}`};
    `}
`;

const SiblingResultContainer = styled.div`
    margin-top: 6px;
`;

const ListItem = styled.div<{ isSelectMode: boolean }>`
    display: flex;
    align-items: center;
    padding: 0px;
`;

type Props = {
    query: string;
    searchResults: CombinedSearchResult[];
    totalResultCount: number;
    isSelectMode: boolean;
    selectedEntities: EntityAndType[];
    setSelectedEntities: (entities: EntityAndType[]) => any;
    suggestions: SearchSuggestion[];
};

export const SearchResultList = ({
    query,
    searchResults,
    totalResultCount,
    isSelectMode,
    selectedEntities,
    setSelectedEntities,
    suggestions,
}: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const selectedEntityUrns = selectedEntities.map((entity) => entity.urn);
    const showSearchFiltersV2 = useIsSearchV2();

    const onClickResult = (result: SearchResult, index: number) => {
        analytics.event({
            type: EventType.SearchResultClickEvent,
            query,
            entityUrn: result.entity.urn,
            entityType: result.entity.type,
            index,
            total: totalResultCount,
        });
    };

    /**
     * Invoked when a new entity is selected. Simply updates the state of the list of selected entities.
     */
    const onSelectEntity = (selectedEntity: EntityAndType, selected: boolean) => {
        if (selected) {
            setSelectedEntities?.([...selectedEntities, selectedEntity]);
        } else {
            setSelectedEntities?.(selectedEntities?.filter((entity) => entity.urn !== selectedEntity.urn) || []);
        }
    };

    return (
        <>
            <ResultList<React.FC<ListProps<CombinedSearchResult>>>
                id="search-result-list"
                isSelectMode={isSelectMode}
                dataSource={searchResults}
                split={false}
                locale={{ emptyText: <EmptySearchResults suggestions={suggestions} /> }}
                renderItem={(item, index) => {
                    const isReadOnly =
                        item.entity.type === EntityType.Dataset && (item.entity as any)?.readOnly?.readOnly === true;
                    return (
                        <ResultWrapper
                            showUpdatedStyles={showSearchFiltersV2}
                            className={`entityUrn-${item.entity.urn}`}
                            role="listitem"
                        >
                            <ListItem
                                isSelectMode={isSelectMode}
                                onClick={() => onClickResult(item, index)}
                                // class name for counting in test purposes only
                                className="test-search-result"
                            >
                                {isSelectMode && (
                                    <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                                        <StyledCheckbox
                                            checked={selectedEntityUrns.indexOf(item.entity.urn) >= 0}
                                            disabled={isReadOnly}
                                            onChange={(e) =>
                                                onSelectEntity(
                                                    { urn: item.entity.urn, type: item.entity.type },
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </Tooltip>
                                )}
                                {entityRegistry.renderSearchResult(item.entity.type, item)}
                            </ListItem>
                            {/* an entity is always going to be inserted in the sibling group, so if the sibling group is just one do not 
                        render. */}
                            {item.matchedEntities && item.matchedEntities.length > 1 && (
                                <SiblingResultContainer className="test-search-result-sibling-section">
                                    <CompactEntityNameList
                                        linkUrlParams={{ [SEPARATE_SIBLINGS_URL_PARAM]: true }}
                                        entities={item.matchedEntities}
                                    />
                                </SiblingResultContainer>
                            )}
                            {!showSearchFiltersV2 && <ThinDivider />}
                        </ResultWrapper>
                    );
                }}
            />
        </>
    );
};
